import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { MediaPageShell } from './components/MediaPageShell';
import { TraditionalCoverageView } from './components/TraditionalCoverageView';
import { useApiTrackedKeywords, useCoverageItems, publishedRangeParams } from './coverage-api';
import { formatCompact } from './fixtures';
import type { MediaKind } from '@/client/types/media';
import { publicMetrics } from '@/client/types/media';

/**
 * Traditional coverage — publications *and* broadcast clips.
 *
 * This page previously hard-filtered `kind === 'publication'`, which made every
 * `kind: 'clip'` item unreachable from anywhere in the app: the retired
 * `/admin/media/traditional/clips` route redirects here, so TV, radio and
 * podcast segments were silently dropped rather than shown. A separate clips
 * page still existed on disk at the time but was neither routed nor imported;
 * it has since been removed in favour of this filter.
 *
 * Rather than restore a second near-identical page, the kind is a filter —
 * `?kind=publication|clip|all` — which is what the legacy redirect now points at
 * and what the PRD's open question (§10.1) was asking for.
 */

/**
 * The only kinds that appear on a traditional channel. Narrower than `MediaKind`,
 * which also covers `post` and `mention` — those are social and belong to
 * Radar's social coverage view, so letting them into this tab set would offer
 * filters that can never match anything here.
 */
type TraditionalKind = Extract<MediaKind, 'publication' | 'clip'>;
type KindFilter = 'all' | TraditionalKind;

const KIND_TABS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All coverage' },
  { value: 'publication', label: 'Publications' },
  { value: 'clip', label: 'Clips' },
];

const TRADITIONAL_KINDS: TraditionalKind[] = ['publication', 'clip'];

/** Narrowing guard, so membership tests do not need a cast at each call site. */
const isTraditionalKind = (k: MediaKind): k is TraditionalKind =>
  k === 'publication' || k === 'clip';

const COPY: Record<string, { title: string; description: string }> = {
  all: {
    title: 'Press clips',
    description:
      'Earned articles and broadcast segments you track — not placements you queued from Studio.',
  },
  publication: {
    title: 'Publications',
    description: 'Articles and long-form placements — filter by outlet, medium and tracked keywords.',
  },
  clip: {
    title: 'Clips & broadcast',
    description: 'TV, radio and podcast segments — filter by outlet and tracked keywords.',
  },
};

const TraditionalPublicationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('kind');
  const kind: KindFilter = raw === 'clip' || raw === 'publication' ? raw : 'all';

  const activeKinds: MediaKind | MediaKind[] = kind === 'all' ? TRADITIONAL_KINDS : kind;

  const { items: allItems } = useCoverageItems({
    channel: 'traditional',
    ...publishedRangeParams(90),
  });
  const items = useMemo(
    () =>
      allItems.filter(
        (i) =>
          i.channel === 'traditional' &&
          (kind === 'all' ? isTraditionalKind(i.kind) : i.kind === kind),
      ),
    [allItems, kind],
  );

  const keywords = useApiTrackedKeywords().keywords.filter(
    (k) => k.enabled && k.channels.includes('traditional'),
  );
  const unreviewed = items.filter((i) => i.status === 'new').length;
  const negative = items.filter((i) => i.sentiment === 'negative').length;
  const totalReach = items.reduce((s, i) => {
    const m = publicMetrics(i);
    return s + (m.reach ?? m.views ?? 0);
  }, 0);

  // Counts per tab, so a kind with nothing in it is visibly empty rather than
  // looking like a page that failed to load.
  const counts: Record<KindFilter, number> = useMemo(() => {
    const traditional = allItems.filter((i) => i.channel === 'traditional');
    return {
      all: traditional.filter((i) => isTraditionalKind(i.kind)).length,
      publication: traditional.filter((i) => i.kind === 'publication').length,
      clip: traditional.filter((i) => i.kind === 'clip').length,
    };
  }, [allItems]);

  const setKind = (next: KindFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('kind');
    else params.set('kind', next);
    // `replace` so flipping tabs does not fill the back button with filter states.
    setSearchParams(params, { replace: true });
  };

  const copy = COPY[kind];

  return (
    <MediaPageShell
      title={copy.title}
      description={copy.description}
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Coverage' },
        { label: 'Press clips' },
      ]}
      icon={BookOpen}
      actions={
        <Button
          type="button"
          className="gap-2"
          onClick={() => toast.message('Add coverage by URL (mock)')}
        >
          <Plus className="h-4 w-4" /> Add coverage
        </Button>
      }
      stats={[
        { label: copy.title, value: String(items.length) },
        { label: 'Unreviewed', value: String(unreviewed) },
        { label: 'Negative', value: String(negative), hint: 'Needs triage' },
        {
          label: 'Trackers on',
          value: String(keywords.length),
          hint: `Reach ${formatCompact(totalReach)}`,
        },
      ]}
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {KIND_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setKind(tab.value)}
            className={`rounded-full border px-3 py-1 text-sm ${
              kind === tab.value
                ? 'border-blue-400 bg-blue-50 text-blue-700'
                : 'border-gray-300 text-gray-600 hover:border-blue-300'
            }`}
          >
            {tab.label}
            <span className="ml-1.5 text-xs text-gray-400">{counts[tab.value]}</span>
          </button>
        ))}
      </div>

      <TraditionalCoverageView
        kind={activeKinds}
        defaultStatus="all"
        enableKeywordTracking
      />
    </MediaPageShell>
  );
};

export default TraditionalPublicationsPage;
